import React, { JSX, useEffect, useRef, useState } from 'react';
import ProviderHero from './ProviderHero';
import ProviderBio from './ProviderBio';
import ProviderLocations from './ProviderLocations';
import ProviderSchedulingWidget from './ProviderSchedulingWidget';
import ActionBar from '../shared/ActionBar';
import Sidebar from '../shared/Sidebar';
import { ProviderFields } from 'components/types/ProviderTypes';
import { ScheduleDay } from 'components/types/ScheduleTypes';
import axios from 'axios';
import Constants from '../../../assets/utils/constant';
import Insurance from '../shared/Insurance';
import { useRouter } from 'next/router';
import { rawRequest, gql } from 'graphql-request';
import { getDeliveryUrl, getDeliveryKey } from 'lib/util';
import { ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import { myChart } from '../shared/MyChartConfig';

type DeptVisitTypeMap = {
  [deptId: string]: {
    visitTypeId: string;
    firstAvailableDate: string;
  };
};

type ProviderAnchorItem = {
  id: string;
  label: string;
  isVisible: boolean;
};

type ProviderAnchorBarProps = {
  items: ProviderAnchorItem[];
};

const VISIT_TYPE_IDS = ['40045', '40059', '1003'];

const ProviderAnchorBar = ({
  items,
}: ProviderAnchorBarProps): JSX.Element | null => {
  const visibleItems = items.filter(item => item.isVisible);
  const visibleItemIds = visibleItems.map(item => item.id);
  const visibleItemKey = visibleItemIds.join('|');

  const anchorBarContainerRef = useRef<HTMLDivElement>(null);

  const [activeId, setActiveId] = useState(visibleItemIds[0] || '');

  useEffect(() => {
    if (visibleItemIds.length === 0) {
      setActiveId('');
      return;
    }

    setActiveId(currentId =>
      visibleItemIds.includes(currentId) ? currentId : visibleItemIds[0]
    );

    let animationFrameId: number | null = null;

    const updateActiveSection = (): void => {
      const anchorBarContainer = anchorBarContainerRef.current;

      if (!anchorBarContainer) {
        return;
      }

      /*
       * Uses the actual computed sticky top value and the
       * rendered anchor-bar height. This works across all
       * responsive breakpoints.
       */
      const computedStyles = window.getComputedStyle(anchorBarContainer);

      const stickyTop = Number.parseFloat(computedStyles.top) || 0;

      const activationLine = stickyTop + anchorBarContainer.offsetHeight + 1;

      /*
       * Make sure the last available section becomes active
       * when the user reaches the bottom of the page.
       */
      const hasReachedPageBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 2;

      if (hasReachedPageBottom) {
        const lastVisibleId = visibleItemIds[visibleItemIds.length - 1];

        setActiveId(currentId =>
          currentId === lastVisibleId ? currentId : lastVisibleId
        );

        return;
      }

      let currentSectionId = visibleItemIds[0];

      visibleItemIds.forEach(sectionId => {
        const section = document.getElementById(sectionId);

        if (!section) {
          return;
        }

        const sectionTop = section.getBoundingClientRect().top;

        if (sectionTop <= activationLine) {
          currentSectionId = sectionId;
        }
      });

      setActiveId(currentId =>
        currentId === currentSectionId ? currentId : currentSectionId
      );
    };

    const handleViewportChange = (): void => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        updateActiveSection();
        animationFrameId = null;
      });
    };

    updateActiveSection();

    window.addEventListener('scroll', handleViewportChange, {
      passive: true,
    });

    window.addEventListener('resize', handleViewportChange);

    window.addEventListener('hashchange', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', handleViewportChange);

      window.removeEventListener('resize', handleViewportChange);

      window.removeEventListener('hashchange', handleViewportChange);

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [visibleItemKey]);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div ref={anchorBarContainerRef} className="provider-anchor-bar-container">
      <nav className="provider-anchor-bar" aria-label="Provider page sections">
        <ul className="provider-anchor-bar-list">
          {visibleItems.map(item => {
            const isActive = activeId === item.id;

            return (
              <li key={item.id} className="provider-anchor-bar-item">
                <a
                  className={`provider-anchor-bar-link ${
                    isActive ? 'is-active' : ''
                  }`}
                  href={`#${item.id}`}
                  aria-current={isActive ? 'location' : undefined}
                  onClick={() => setActiveId(item.id)}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

const buildContentHubProviderImageUrl = (
  firstName?: string,
  lastName?: string,
  npi?: string
): string => {
  if (!firstName || !lastName || !npi) {
    return '';
  }

  const cleanValue = (value: string): string =>
    value
      .trim()
      .toLowerCase()
      .replace(/\.\s+/g, '.')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9.-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

  return `${Constants.api.contentHubProviderImageBaseUrl}/${cleanValue(
    firstName
  )}-${cleanValue(lastName)}-${npi}.jpg`;
};

const getVisitTypeIdFromScheduleDay = (day: any): string => {
  const externalVisitTypeId = day?.VisitType?.IDs?.find(
    (idObj: any) => idObj?.Type === 'External'
  )?.ID;

  const anyMatchingVisitTypeId = day?.VisitType?.IDs?.find((idObj: any) =>
    VISIT_TYPE_IDS.includes(String(idObj?.ID).trim())
  )?.ID;

  return String(
    externalVisitTypeId || anyMatchingVisitTypeId || day?.VisitType?.ID || ''
  ).trim();
};

const hasTextValue = (value?: string): boolean => Boolean(value?.trim());

const ProviderProfile = (): JSX.Element => {
  const router = useRouter();
  const routerQueryPath = router.query.path as Array<string>;

  const providerItemName = routerQueryPath?.length
    ? routerQueryPath[routerQueryPath.length - 1]
    : routerQueryPath?.[1];

  const providerId = providerItemName?.substring(
    providerItemName.lastIndexOf('-') + 1,
    providerItemName.length
  );

  const [scheduleDays, setScheduleDays] = useState<Array<ScheduleDay>>([]);
  const [deptDays, setDeptDays] = useState<{
    [key: string]: string[];
  }>({});
  const [deptVisitTypes, setDeptVisitTypes] = useState<DeptVisitTypeMap>({});
  const [providerSchedulable, setProviderSchedulable] = useState(false);
  const [fields, setFields] = useState<ProviderFields>({} as ProviderFields);

  const specialties = fields?.Specialty?.value
    ?.split('|')
    .map(specialty => specialty.trim())
    .filter(specialty => specialty.length > 0);

  const certifications = fields?.Certifications?.targetItems;
  const trainings = fields?.Trainings?.targetItems;

  const hasBiographyContent =
    hasTextValue(fields?.AboutTheProvider?.value) ||
    hasTextValue(fields?.PersonalInterests?.value) ||
    hasTextValue(fields?.VideoUrl?.value);

  const hasBackgroundContent =
    Boolean(specialties?.length) ||
    Boolean(certifications?.length) ||
    Boolean(trainings?.length) ||
    hasTextValue(fields?.SpokenLanguages?.value) ||
    hasTextValue(fields?.Gender?.value);

  const hasLocationsContent = Boolean(fields?.Locations?.value?.length);

  /*
   * Insurance currently does not receive a provider-specific field.
   * Because the component always renders content, the anchor is visible.
   * Replace this with an actual field check if Insurance becomes
   * provider-specific.
   */
  const hasInsuranceContent = true;

  const providerAnchorItems: ProviderAnchorItem[] = [
    {
      id: 'provider-biography',
      label: 'Biography',
      isVisible: hasBiographyContent,
    },
    {
      id: 'provider-background',
      label: 'Background',
      isVisible: hasBackgroundContent,
    },
    {
      id: 'provider-locations',
      label: 'Locations',
      isVisible: hasLocationsContent,
    },
    {
      id: 'provider-insurance',
      label: 'Insurance',
      isVisible: hasInsuranceContent,
    },
  ];

  const fallbackImage =
    fields?.Gender?.value === 'Male'
      ? Constants.fallbackImages.providerMale
      : Constants.fallbackImages.providerFemale;

  const contentHubProviderImageUrl = buildContentHubProviderImageUrl(
    fields?.FirstName?.value,
    fields?.LastName?.value,
    fields?.NPI?.value
  );

  const contentHubProviderImage = {
    value: `<img
      src="${contentHubProviderImageUrl || fallbackImage}"
      alt="${fields?.FullName?.value || 'Provider Image'}"
      onerror="this.onerror=null;this.src='${fallbackImage}';"
    />`,
  } as unknown as ImageField;

  const deliveryKey = getDeliveryKey();
  const endpoint = getDeliveryUrl();

  const providerQuery = gql`
    query ($externalID: String!) {
      search(
        where: {
          AND: [
            {
              name: "_path"
              value: "{6A68FA22-BE0C-46BB-912C-22F1F83E28DC}"
              operator: CONTAINS
            }
            { name: "ExternalID", value: $externalID, operator: EQ }
          ]
        }
        first: 1
      ) {
        results {
          id
          name

          Certifications: field(name: "Certifications") {
            ... on MultilistField {
              targetItems {
                id
                BoardName: field(name: "BoardName") {
                  value
                }
                SpecialtyName: field(name: "SpecialtyName") {
                  value
                }
                YearCertified: field(name: "YearCertified") {
                  value
                }
              }
            }
          }

          Trainings: field(name: "Training") {
            ... on MultilistField {
              targetItems {
                id
                FieldOfStudy: field(name: "FieldOfStudy") {
                  value
                }
                GraduationYear: field(name: "GraduationYear") {
                  value
                }
                TrainingName: field(name: "TrainingName") {
                  value
                }
                TrainingType: field(name: "TrainingType") {
                  value
                }
              }
            }
          }

          displayName

          SortField: field(name: "SortField") {
            value
          }

          externalIDfield: field(name: "ExternalID") {
            value: jsonValue
          }

          AboutTheProvider: field(name: "AboutTheProvider") {
            value: value
          }

          AcceptingNewPatients: field(name: "AcceptingNewPatients") {
            value: value
          }

          BookOnlineOverrideUrl: field(name: "BookOnlineOverrideUrl") {
            value: value
          }

          ClinicalFocus: field(name: "Clinical Focus") {
            value: jsonValue
          }

          Degrees: field(name: "Degrees") {
            value: value
          }

          EpicId: field(name: "EpicId") {
            value: value
          }

          FirstName: field(name: "FirstName") {
            value
          }

          LastName: field(name: "LastName") {
            value
          }

          NPI: field(name: "NPI") {
            value
          }

          FullName: field(name: "FullName") {
            value
          }

          Gender: field(name: "Gender") {
            value
          }

          KSBProvider: field(name: "KSBProvider") {
            value
          }

          Locations: field(name: "Locations") {
            value: jsonValue
          }

          OnlineScheduling: field(name: "OnlineScheduling") {
            value
          }

          PersonalInterests: field(name: "PersonalInterests") {
            value
          }

          ProviderImage: field(name: "ProviderImage") {
            value: value
          }

          Rating: field(name: "Rating") {
            value: value
          }

          ReviewCount: field(name: "ReviewCount") {
            value: value
          }

          Reviews: field(name: "Reviews") {
            value: value
          }

          Specialties: field(name: "Specialties") {
            value: jsonValue
          }

          Specialty: field(name: "Specialty") {
            value: value
          }

          SpokenLanguages: field(name: "SpokenLanguages") {
            value: value
          }

          Training: field(name: "Training") {
            value: jsonValue
          }

          VideoUrl: field(name: "VideoUrl") {
            value: value
          }
        }
      }
    }
  `;

  const variables = {
    externalID: providerId,
  };

  const headers = {
    sc_apikey: deliveryKey,
  };

  const getProvider = async (): Promise<void> => {
    try {
      const { data } = await rawRequest(
        endpoint,
        providerQuery,
        variables,
        headers
      );

      const provider = data?.search?.results?.[0];

      if (provider) {
        setFields(provider as ProviderFields);
      }
    } catch (error) {
      console.error('Error fetching provider:', error);
    }
  };

  const getDeptScheduleInfo = (deptId: string) => {
    const filteredScheduleDays = scheduleDays
      .filter(
        (day: any) => day?.Department?.ID === deptId && day?.Slots?.length > 0
      )
      .sort(
        (a: ScheduleDay, b: ScheduleDay) =>
          new Date(a.Date).getTime() - new Date(b.Date).getTime()
      );

    const availableDates = filteredScheduleDays
      .slice(0, 3)
      .map(day => day.Date);

    const firstAvailableDay = filteredScheduleDays[0] as any;

    const visitTypeId = getVisitTypeIdFromScheduleDay(firstAvailableDay);

    return {
      availableDates,
      visitTypeId,
      firstAvailableDate: firstAvailableDay?.Date || '',
    };
  };

  const mapDeptDates = (): void => {
    const deptDates: {
      [key: string]: string[];
    } = {};

    const deptVisitTypeMap: DeptVisitTypeMap = {};

    let hasAnySchedulableDept = false;

    fields?.Locations?.value?.forEach(location => {
      const deptId = location?.fields?.EpicDeptId?.value;

      if (!deptId) {
        return;
      }

      const deptScheduleInfo = getDeptScheduleInfo(deptId);

      if (deptScheduleInfo.availableDates.length > 0) {
        hasAnySchedulableDept = true;
        deptDates[deptId] = deptScheduleInfo.availableDates;

        if (
          deptScheduleInfo.visitTypeId &&
          deptScheduleInfo.firstAvailableDate
        ) {
          deptVisitTypeMap[deptId] = {
            visitTypeId: deptScheduleInfo.visitTypeId,
            firstAvailableDate: deptScheduleInfo.firstAvailableDate,
          };
        }
      }
    });

    setProviderSchedulable(hasAnySchedulableDept);
    setDeptDays(deptDates);
    setDeptVisitTypes(deptVisitTypeMap);
  };

  const getProviderAppts = async (): Promise<ScheduleDay[]> => {
    const daysIncrements = 30;
    const totalDays = 180;
    const today = new Date();

    let allScheduleDays: ScheduleDay[] = [];

    const formatDate = (date: Date): string => date.toISOString().split('T')[0];

    const requiredDeptIds =
      fields?.Locations?.value
        ?.map(location => location?.fields?.EpicDeptId?.value)
        .filter((deptId): deptId is string => Boolean(deptId)) || [];

    const hasThreeDatesForEachDept = (
      currentScheduleDays: ScheduleDay[]
    ): boolean =>
      requiredDeptIds.every(deptId => {
        const availableDates = currentScheduleDays.filter(
          (day: any) => day?.Department?.ID === deptId && day?.Slots?.length > 0
        );

        return availableDates.length >= 3;
      });

    try {
      for (let startDay = 0; startDay < totalDays; startDay += daysIncrements) {
        const currentStartDate = new Date(today);

        currentStartDate.setDate(today.getDate() + startDay);

        const currentEndDate = new Date(today);

        currentEndDate.setDate(
          today.getDate() + Math.min(startDay + daysIncrements, totalDays)
        );

        const response = await axios.post(
          `${myChart.epicApi}${myChart.getScheduleDays}`,
          {
            StartDate: formatDate(currentStartDate),
            EndDate: formatDate(currentEndDate),
            ProviderID: fields?.EpicId?.value,
            ProviderIDType: 'External',
            VisitTypeIDs: VISIT_TYPE_IDS.map(id => ({
              ID: id,
              Type: 'External',
            })),
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Epic-Client-ID': myChart.epicClientID,
              Accept: 'application/json',
              Authorization: `Basic ${myChart.epicAccessToken}`,
            },
          }
        );

        allScheduleDays = allScheduleDays.concat(
          response.data?.ScheduleDays || []
        );

        if (
          requiredDeptIds.length > 0 &&
          hasThreeDatesForEachDept(allScheduleDays)
        ) {
          break;
        }
      }

      return allScheduleDays;
    } catch (error) {
      console.error('Error fetching next appointments:', error);

      throw error;
    }
  };

  useEffect(() => {
    if (providerId) {
      getProvider();
    }
  }, [providerId]);

  useEffect(() => {
    if (!fields?.EpicId?.value) {
      return;
    }

    getProviderAppts()
      .then(providerScheduleDays => {
        setScheduleDays(providerScheduleDays);
      })
      .catch(() => {
        setScheduleDays([]);
      });
  }, [fields?.EpicId?.value]);

  useEffect(() => {
    mapDeptDates();
  }, [scheduleDays, fields?.Locations?.value]);

  return (
    <>
      <div className="profile">
        <ProviderHero
          img={contentHubProviderImage}
          name={fields?.FullName?.value}
          degrees={fields?.Degrees?.value}
          specialties={specialties}
          clinicalFocus={fields?.ClinicalFocus?.value}
          rating={fields?.Rating?.value}
          reviewCount={fields?.ReviewCount?.value}
          accepting={fields?.AcceptingNewPatients?.value === '1'}
          locations={fields?.Locations?.value}
          gender={fields?.Gender?.value}
          sortField={fields?.SortField}
        />

        <div className="overhauled">
          <div className="jss-inner-container">
            <div className="profile-layout">
              <div className="left-col">
                <ProviderAnchorBar items={providerAnchorItems} />

                {(hasBiographyContent || hasBackgroundContent) && (
                  <ProviderBio
                    biographySectionId="provider-biography"
                    backgroundSectionId="provider-background"
                    background={fields?.PersonalInterests}
                    specialties={specialties}
                    gender={fields?.Gender?.value}
                    spokenLanguages={fields?.SpokenLanguages?.value?.replace(
                      /\|/g,
                      ', '
                    )}
                    certifications={
                      certifications
                        ? [...certifications].sort((a, b) =>
                            (b?.YearCertified?.value || '').localeCompare(
                              a?.YearCertified?.value || ''
                            )
                          )
                        : []
                    }
                    trainings={
                      trainings
                        ? [...trainings].sort((a, b) =>
                            (b?.GraduationYear?.value || '').localeCompare(
                              a?.GraduationYear?.value || ''
                            )
                          )
                        : []
                    }
                    bio={fields?.AboutTheProvider}
                    videoUrl={fields?.VideoUrl?.value}
                  />
                )}

                {hasLocationsContent && (
                  <section
                    id="provider-locations"
                    className="provider-anchor-target"
                    aria-label="Provider locations"
                  >
                    <ProviderLocations
                      locations={fields?.Locations?.value}
                      providerId={fields?.EpicId?.value}
                      providerName={fields?.FullName?.value}
                      deptDays={deptDays}
                    />
                  </section>
                )}

                {hasInsuranceContent && (
                  <section
                    id="provider-insurance"
                    className="provider-anchor-target"
                    aria-label="Insurance"
                  >
                    <Insurance />
                  </section>
                )}

                {/* <ProviderReviews name="reviews" /> */}

                <ActionBar print copy suggestEdit />
              </div>

              {hasLocationsContent && fields?.KSBProvider?.value !== '1' ? (
                <div className="right-col">
                  <Sidebar header="Schedule Appointment" pageType="provider">
                    <ProviderSchedulingWidget
                      providerHeading={[
                        fields?.FullName?.value,
                        fields?.Degrees?.value,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                      providerEpicId={fields?.EpicId?.value}
                      locations={fields?.Locations?.value}
                      providerSchedulable={providerSchedulable}
                      deptDays={deptDays}
                      deptVisitTypes={deptVisitTypes}
                    />
                  </Sidebar>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div id="sidebar-modal-root" />
    </>
  );
};

export default ProviderProfile;
